import { useEffect, useMemo, useState } from 'react';
import { useChatSessions } from '../../../features/chat/chatSessions/useChatSessions';
import { TenantUserListPhoto } from '../../../features/user/components/UserListPhoto';
import { formatInitials } from '../../../features/user/userDisplay';
import { useI18n } from '../../../i18n/useI18n';
import { LiksConfirmDialog } from '../LiksConfirmDialog/LiksConfirmDialog';
import './ChatSessionDeleteDialog.css';

type ChatSessionDeleteDialogProps = {
  isOpen: boolean;
  sessionId: string | null;
  sessionTitle: string;
  isOwner: boolean;
  isBusy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function ChatSessionDeleteDialog({
  isOpen,
  sessionId,
  sessionTitle,
  isOwner,
  isBusy = false,
  onConfirm,
  onCancel,
}: ChatSessionDeleteDialogProps) {
  const { translate } = useI18n();
  const { listChatMembers } = useChatSessions();

  const [members, setMembers] = useState<
    Awaited<ReturnType<typeof listChatMembers>>
  >([]);

  useEffect(() => {
    if (!isOpen || sessionId === null) {
      return;
    }

    void (async () => {
      const nextMembers = await listChatMembers(sessionId);
      setMembers(nextMembers);
    })();
  }, [isOpen, listChatMembers, sessionId]);

  const otherParticipants = useMemo(
    () => members.filter((member) => member.chatRole === 'participant'),
    [members],
  );

  const hasOtherParticipants = isOwner && otherParticipants.length > 0;

  const dialogTitle = isOwner
    ? translate('chatSessions.deleteTitle')
    : translate('chatSessions.leaveTitle');

  const dialogMessage = isOwner
    ? translate('chatSessions.deleteMessage', { title: sessionTitle })
    : translate('chatSessions.leaveMessage', { title: sessionTitle });

  const confirmLabel = isOwner
    ? translate('chatSessions.deleteConfirm')
    : translate('chatSessions.leaveConfirm');

  return (
    <LiksConfirmDialog
      isOpen={isOpen && sessionId !== null}
      title={dialogTitle}
      message={
        <>
          <p>{dialogMessage}</p>
          {hasOtherParticipants && (
            <div className="liks-chat-delete__warning">
              <p className="liks-chat-delete__warning-text">
                {translate('chatSessions.deleteSharedWarning')}
              </p>
              <ul className="liks-chat-delete__warning-list">
                {otherParticipants.map((member) => (
                  <li
                    key={member.userId}
                    className="liks-chat-delete__warning-item"
                  >
                    <TenantUserListPhoto
                      thumbnail={member.thumbnail}
                      initials={formatInitials(member.name)}
                      className="liks-chat-delete__photo"
                    />
                    <div className="liks-chat-delete__warning-info">
                      <span className="liks-chat-delete__warning-name">
                        {member.name}
                      </span>
                      <span className="liks-chat-delete__warning-meta">
                        {member.email}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      }
      confirmLabel={confirmLabel}
      cancelLabel={
        isOwner
          ? translate('chatSessions.deleteCancel')
          : translate('chatSessions.leaveCancel')
      }
      confirmVariant={isOwner ? 'primary' : 'danger'}
      isBusy={isBusy}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
